// src/context/AuthContext.js
import { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../api/wbuoaApi';
import { clearAuthSession, loadAuthSession, saveAuthSession } from '../utils/authStorage';
import { getApiErrorMessage } from '../utils/apiError';

const AuthContext = createContext();

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const session = loadAuthSession();
        if (session?.user) {
            setUser(session.user);
            setToken(session.token || null);
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        const handleUnauthorized = () => {
            clearAuthSession();
            setUser(null);
            setToken(null);
        };

        window.addEventListener('unauthorized', handleUnauthorized);
        return () => window.removeEventListener('unauthorized', handleUnauthorized);
    }, []);

    const login = async (email, password) => {
        try {
            setLoading(true);
            setError(null);
            const response = await authAPI.login(email, password);
            const userData = response.user || response.data || response;
            const authToken = response.token || response.access_token || response.data?.token || null;

            setUser(userData);
            setToken(authToken);
            saveAuthSession({ user: userData, token: authToken });

            return response;
        } catch (err) {
            const errorMessage = getApiErrorMessage(err, 'Login failed. Please try again.');
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const register = async (registrationData) => {
        try {
            setLoading(true);
            setError(null);
            const response = await authAPI.register(registrationData);
            return response;
        } catch (err) {
            const errorMessage = getApiErrorMessage(err, 'Registration failed. Please try again.');
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const logout = () => {
        setUser(null);
        setToken(null);
        setError(null);
        clearAuthSession();
        return { success: true };
    };

    const value = {
        user,
        token,
        isAuthenticated: Boolean(user),
        loading,
        error,
        login,
        register,
        logout,
        setUser,
        setError,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
